import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusCircle, Edit, Trash2, Save, LogOut } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { useAuth } from '../context/AuthContext';
import { articlesAPI, categoriesAPI } from '../services/api';

const CATEGORIES = [
  { id: 1, name: 'Apps', slug: 'apps' },
  { id: 2, name: 'Inteligencia Artificial', slug: 'inteligencia-artificial' },
  { id: 3, name: 'Negocios Online', slug: 'negocios-online' },
  { id: 4, name: 'Consejos', slug: 'consejos' }
];

export const AdminPanel = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingArticle, setEditingArticle] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('articles');

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: 'apps',
    author: user?.name || 'Admin',
    image: '',
    tags: '',
    read_time: '5 min'
  });

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      const data = await articlesAPI.getAll();
      setArticles(data);
    } catch (error) {
      console.error('Error loading articles:', error);
      toast.error('Error al cargar artículos');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Auto-generate slug from title
    if (name === 'title' && !editingArticle) {
      const slug = value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      category: 'apps',
      author: user?.name || 'Admin',
      image: '',
      tags: '',
      read_time: '5 min'
    });
    setEditingArticle(null);
    setIsCreating(false);
    setActiveTab('articles');
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingArticle(null);
    resetForm();
    setActiveTab('editor');
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
    setIsCreating(false);
    setFormData({
      title: article.title,
      slug: article.slug,
      excerpt: article.excerpt,
      content: article.content.replace(/<[^>]*>/g, '\n'),
      category: article.category,
      author: article.author,
      image: article.image,
      tags: article.tags.join(', '),
      read_time: article.read_time
    });
    setActiveTab('editor');
  };

  const handleSave = async () => {
    if (!formData.title || !formData.excerpt || !formData.content) {
      toast.error('Por favor completa los campos obligatorios');
      return;
    }

    try {
      const tagsArray = formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
      
      const articleData = {
        title: formData.title,
        slug: formData.slug || formData.title.toLowerCase().replace(/\s+/g, '-'),
        excerpt: formData.excerpt,
        content: `<p>${formData.content.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br>')}</p>`,
        category: formData.category,
        author: formData.author || user?.name || 'Admin',
        image: formData.image || 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=800&q=80',
        tags: tagsArray,
        read_time: formData.read_time || '5 min'
      };

      if (editingArticle) {
        // Update existing article
        await articlesAPI.update(editingArticle._id, articleData);
        toast.success('Artículo actualizado correctamente');
      } else {
        // Create new article
        await articlesAPI.create(articleData);
        toast.success('Artículo creado correctamente');
      }

      // Reload articles
      await loadArticles();
      resetForm();
    } catch (error) {
      console.error('Error saving article:', error);
      const errorMsg = error.response?.data?.detail || 'Error al guardar artículo';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error al guardar artículo');
    }
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este artículo?')) {
      return;
    }

    try {
      await articlesAPI.delete(articleId);
      toast.success('Artículo eliminado correctamente');
      await loadArticles();
    } catch (error) {
      console.error('Error deleting article:', error);
      toast.error('Error al eliminar artículo');
    }
  };

  const handleLogout = async () => {
    const result = await logout();
    if (result.success) {
      navigate('/login');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" />
      
      {/* Admin Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Panel de Administración</h1>
              <p className="text-gray-600 mt-1">Bienvenido, {user?.name || user?.email}</p>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="hover:bg-red-50 hover:text-red-600 hover:border-red-500"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-gray-200">
            <TabsTrigger value="articles">Artículos ({articles.length})</TabsTrigger>
            <TabsTrigger value="editor">
              {isCreating ? 'Crear Artículo' : editingArticle ? 'Editar Artículo' : 'Editor'}
            </TabsTrigger>
          </TabsList>

          {/* Articles List */}
          <TabsContent value="articles" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Todos los Artículos</h2>
              <Button
                onClick={handleCreate}
                className="bg-teal-500 hover:bg-teal-600 text-white"
              >
                <PlusCircle className="h-4 w-4 mr-2" />
                Crear Nuevo
              </Button>
            </div>

            {articles.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <p className="text-gray-500 text-lg">
                    No hay artículos todavía. ¡Crea tu primer artículo!
                  </p>
                  <Button
                    onClick={handleCreate}
                    className="mt-4 bg-teal-500 hover:bg-teal-600 text-white"
                  >
                    <PlusCircle className="h-4 w-4 mr-2" />
                    Crear Primer Artículo
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {articles.map((article) => (
                  <Card key={article._id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <Badge className="bg-teal-500">{article.category_name}</Badge>
                            {article.featured && (
                              <Badge variant="outline" className="border-yellow-500 text-yellow-700">
                                Destacado
                              </Badge>
                            )}
                          </div>
                          <h3 className="text-lg font-bold text-gray-900 mb-2">
                            {article.title}
                          </h3>
                          <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                            {article.excerpt}
                          </p>
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <span>{article.author}</span>
                            <span>•</span>
                            <span>{new Date(article.date).toLocaleDateString('es-ES')}</span>
                            <span>•</span>
                            <span>{article.read_time}</span>
                          </div>
                        </div>
                        <div className="flex space-x-2 ml-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(article)}
                            className="hover:bg-teal-50 hover:text-teal-600 hover:border-teal-500"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(article._id)}
                            className="hover:bg-red-50 hover:text-red-600 hover:border-red-500"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Editor */}
          <TabsContent value="editor">
            <Card>
              <CardHeader>
                <CardTitle>
                  {editingArticle ? 'Editar Artículo' : 'Crear Nuevo Artículo'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="title">Título *</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Título del artículo"
                    className="text-lg"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="slug">Slug (URL)</Label>
                    <Input
                      id="slug"
                      name="slug"
                      value={formData.slug}
                      onChange={handleInputChange}
                      placeholder="mi-articulo-slug"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="category">Categoría</Label>
                    <select
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    >
                      {CATEGORIES.map((category) => (
                        <option key={category.id} value={category.slug}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="excerpt">Extracto *</Label>
                  <Textarea
                    id="excerpt"
                    name="excerpt"
                    value={formData.excerpt}
                    onChange={handleInputChange}
                    placeholder="Breve descripción del artículo"
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="content">Contenido *</Label>
                  <Textarea
                    id="content"
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    placeholder="Contenido completo del artículo"
                    rows={12}
                    className="font-mono text-sm"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="author">Autor</Label>
                    <Input
                      id="author"
                      name="author"
                      value={formData.author}
                      onChange={handleInputChange}
                      placeholder="Nombre del autor"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="read_time">Tiempo de lectura</Label>
                    <Input
                      id="read_time"
                      name="read_time"
                      value={formData.read_time}
                      onChange={handleInputChange}
                      placeholder="5 min"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="tags">Etiquetas (separadas por comas)</Label>
                    <Input
                      id="tags"
                      name="tags"
                      value={formData.tags}
                      onChange={handleInputChange}
                      placeholder="apps, dinero, consejos"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="image">URL de Imagen</Label>
                  <Input
                    id="image"
                    name="image"
                    value={formData.image}
                    onChange={handleInputChange}
                    placeholder="https://ejemplo.com/imagen.jpg"
                  />
                </div>

                <div className="flex space-x-4 pt-4">
                  <Button
                    onClick={handleSave}
                    className="bg-teal-500 hover:bg-teal-600 text-white"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {editingArticle ? 'Actualizar' : 'Crear'} Artículo
                  </Button>
                  <Button
                    onClick={resetForm}
                    variant="outline"
                    className="hover:bg-gray-100"
                  >
                    Cancelar
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
